/**
 * Mapa Leaflet do M2 — pins dos instrumentos (cor por nível, forma por tipo).
 * Regra #11: usar react-leaflet, nunca L.map() direto em useEffect.
 *
 * GUPM-194: o seletor de basemap saiu de dentro do mapa para a barra de controles
 * (MapControls, acima do mapa, fiel ao V8). Aqui só renderizamos o basemap recebido
 * por prop — a base (tile) mais um overlay opcional (rótulos do híbrido, por cima).
 */
import { useEffect, useMemo, useRef } from 'react';
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import L, { type Marker as LeafletMarker } from 'leaflet';
import type { FeatureCollection } from 'geojson';
import type { MapPin } from '../domain/pins';
import { svgDoPino, tamanhoDoPino } from '../domain/pin-svg';
import type { SirenePoint } from '../domain/paebm';
import type { PontoEncontro, ZasEdificacao } from '@/domain';
import { FLY_TO_ZOOM, getBasemap, MAP_MAX_ZOOM, type BasemapMode } from '../domain/basemaps';
import MapOverlays from './MapOverlays';

/** Centro de fallback (Barragem B — Patos de Minas) quando não há pins. */
const FALLBACK_CENTER: [number, number] = [-18.5, -46.5];

/**
 * Forma de cada tipo como SVG (viewBox 0 0 28 28, centrado em 14,14).
 * A forma É o pin — preenchida com a cor do nível, sem círculo de fundo.
 */

function makeIcon(pin: MapPin, selected: boolean): L.DivIcon {
  const size = tamanhoDoPino(selected);
  return L.divIcon({
    className: 'm2-pin',
    html: svgDoPino(pin, selected),
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
}

/**
 * Mantém o tamanho interno do Leaflet sincronizado com o container.
 * Sem isto, o mapa nasce dentro do `<main flex-1>` (cuja largura muda com a
 * animação `transition-all` da sidebar) com tamanho defasado — `flyTo`/`fitBounds`
 * calculam o centro errado e o conteúdo "vai pra esquerda".
 */
function KeepSized() {
  const map = useMap();
  useEffect(() => {
    // Recalcula assim que o layout flex assenta (após o primeiro paint).
    const raf = requestAnimationFrame(() => map.invalidateSize());

    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => map.invalidateSize());
      observer.observe(map.getContainer());
    }
    return () => {
      cancelAnimationFrame(raf);
      observer?.disconnect();
    };
  }, [map]);
  return null;
}

/** Ajusta o viewport para enquadrar tudo que está visível (pins + zonas). */
function FitBounds({ points }: { points: [number, number][] }) {
  const map = useMap();
  // Assinatura estável: re-fita só quando o conjunto de pontos muda de fato
  // (não a cada render nem ao apenas dar pan/zoom manual).
  const sig = points.map((p) => `${p[0]},${p[1]}`).join(';');
  useEffect(() => {
    if (points.length === 0) return;
    map.invalidateSize();
    map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 17 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, sig]);
  return null;
}

/**
 * Voa até o pin selecionado quando a seleção muda — ou quando `focusSeq` avança
 * com a MESMA seleção (reclique na lista, ver `focusSeq` nas props do mapa).
 */
function FlyToSelected({ pin, focusSeq }: { pin: MapPin | null; focusSeq: number }) {
  const map = useMap();
  useEffect(() => {
    if (!pin) return;
    // Garante dimensões atuais antes de centralizar (senão o centro fica defasado).
    map.invalidateSize();
    map.flyTo([pin.lat, pin.lon], FLY_TO_ZOOM, { duration: 0.6 });
  }, [map, pin, focusSeq]);
  return null;
}

interface Props {
  pins: MapPin[];
  selectedId: string | null;
  /**
   * Contador de pedidos de foco (GUPM-230). Existe porque clicar duas vezes na
   * mesma linha da lista não muda `selectedId` — sem ele, o segundo clique não
   * revoaria nem reabriria o card, e a linha pareceria morta depois que o usuário
   * fechasse o popup.
   */
  focusSeq: number;
  onSelect: (id: string) => void;
  /** Basemap ativo (controlado pela barra de controles — GUPM-194). */
  basemap: BasemapMode;
  sirenes: SirenePoint[];
  edificacoes: ZasEdificacao[];
  pontosEncontro: PontoEncontro[];
  mancha: FeatureCollection | null;
  manchaBounds: [[number, number], [number, number]] | null;
  /** ZSS — placeholder elíptico (geometria oficial a fornecer pela Foscentro). */
  zss: boolean;
}

export default function InstrumentMap({
  pins, selectedId, focusSeq, onSelect, basemap, sirenes, edificacoes, pontosEncontro, mancha, manchaBounds, zss,
}: Props) {
  const bm = getBasemap(basemap);
  const selectedPin = useMemo(
    () => pins.find((p) => p.id === selectedId) ?? null,
    [pins, selectedId],
  );

  /**
   * Handles dos marcadores, para abrir o card (Popup) de quem foi selecionado pela
   * LISTA — clicar no pino já abre o dele pelo próprio Leaflet. Sem isto, quem
   * chegava pela lista via o mapa dar zoom e nenhuma identificação do que ficou no
   * centro. O `Popup` do react-leaflet não tem prop de "aberto", então o caminho é
   * a instância do Leaflet.
   */
  const markers = useRef(new Map<string, LeafletMarker>());
  useEffect(() => {
    if (!selectedId) return;
    markers.current.get(selectedId)?.openPopup();
  }, [selectedId, focusSeq]);
  const center: [number, number] = pins[0] ? [pins[0].lat, pins[0].lon] : FALLBACK_CENTER;

  // Pontos para o enquadramento: pins + sirenes + edificações + PEs + bbox da mancha.
  const fitPoints: [number, number][] = [
    ...pins.map((p) => [p.lat, p.lon] as [number, number]),
    ...sirenes.map((s) => [s.lat, s.lon] as [number, number]),
    ...edificacoes.map((e) => [e.lat, e.lon] as [number, number]),
    ...pontosEncontro.map((p) => [p.lat, p.lon] as [number, number]),
    ...(manchaBounds ?? []),
  ];

  return (
    // `leaflet-shell` cria stacking context próprio (GUPM-229): os z-index internos
    // do Leaflet (panes 200–700, controles 800/1000) ficam confinados aqui em vez
    // de competir no contexto raiz e pintar por cima das modais do app (z-50..100).
    <div className="leaflet-shell relative rounded-xl overflow-hidden border border-jf-border" style={{ height: 460 }}>
      <MapContainer center={center} zoom={13} maxZoom={MAP_MAX_ZOOM} scrollWheelZoom style={{ height: '100%', width: '100%' }}>
        <TileLayer
          key={bm.base.url}
          attribution={bm.base.attribution}
          url={bm.base.url}
          subdomains={bm.base.subdomains ?? []}
          maxZoom={bm.base.maxZoom}
          maxNativeZoom={bm.base.maxNativeZoom}
        />
        {bm.overlay && (
          // Overlay de rótulos do híbrido — transparente, renderizado por cima da base.
          <TileLayer
            key={bm.overlay.url}
            attribution={bm.overlay.attribution}
            url={bm.overlay.url}
            subdomains={bm.overlay.subdomains ?? []}
            maxZoom={bm.overlay.maxZoom}
            maxNativeZoom={bm.overlay.maxNativeZoom}
            zIndex={2}
          />
        )}
        <KeepSized />
        <FitBounds points={fitPoints} />
        <FlyToSelected pin={selectedPin} focusSeq={focusSeq} />
        <MapOverlays sirenes={sirenes} edificacoes={edificacoes} pontosEncontro={pontosEncontro} mancha={mancha} zss={zss} />
        {pins.map((pin) => (
          <Marker
            key={pin.id}
            position={[pin.lat, pin.lon]}
            icon={makeIcon(pin, pin.id === selectedId)}
            eventHandlers={{ click: () => onSelect(pin.id) }}
            ref={(m) => {
              if (m) markers.current.set(pin.id, m);
              else markers.current.delete(pin.id);
            }}
          >
            <Popup>
              <div style={{ minWidth: 150 }}>
                <div style={{ fontWeight: 700, color: pin.color, fontFamily: 'monospace' }}>{pin.code}</div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{pin.label}</div>
                <div style={{ fontSize: 11, marginTop: 4 }}>
                  Nível: <strong style={{ color: pin.color }}>{pin.level}</strong>
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
